import {QueryObserverResult, UseQueryResult} from "@tanstack/react-query";
import {cleanse} from "../util/arutil.tsx";
import {TSTZ_NEGATIVE_INFINITY} from "../tstzspan.ts";
import {isAfter} from "date-fns";

// this always gives data back as an array as large as the number of queries passed
// everything that has not settled will have undefined in its slot in the data array
// status and error are laid out in parallel
export function mergeUseQueriesWithInfill<T, TError>(tqrvs: UseQueryResult<T, TError>[]) {
    const data: (T | undefined)[] = [];
    const statusen: QueryObserverResult["status"][] = [];
    const errors: (TError | null)[] = [];
    let dataUpdatedAt = TSTZ_NEGATIVE_INFINITY;

    tqrvs.forEach((tqrv, ix) => {
        data[ix] = tqrv.data;
        statusen[ix] = tqrv.status;
        errors[ix] = tqrv.error;
        if (isAfter(tqrv.dataUpdatedAt, dataUpdatedAt)) {
            dataUpdatedAt = tqrv.dataUpdatedAt;
        }
    });

    let isPending = false;
    let isSuccess = true;
    let isError = false;

    ALPHA: for (let ix = 0; ix < data.length; ++ix) {
        switch (statusen[ix]) {
            case "pending":
                isPending = true;
                isSuccess = false;
                isError = false;
                break ALPHA;

            case "success":
                continue;

            case "error":
                if (errors[ix]) {
                    isPending = false;
                    isSuccess = false;
                    isError = true;
                }
        }
    }

    const status =
        isError ? "error"
            : isPending ? "pending"
                : isSuccess ? "success" : "error";

    return {
        data: isSuccess ? cleanse(data) : data, dataUpdatedAt,
        statusen, errors, isSuccess, isError, isPending, status
    };
}

// this collapses everything to what we have, so the ordering of data is not guaranteed
// to match the ordering of the queries you pass in. status bleeds through. only the
// first error encountered is returned.
export function mergeUseQueriesWithCollapse<T, TError>(tqrvs: UseQueryResult<T, TError>[]) {
    const {data: idata, statusen: istatusen, errors: ierrors} = mergeUseQueriesWithInfill(tqrvs);
    const data = cleanse(idata);
    const error = ierrors.find(maybe => !!maybe);
    const allSuccess = error ? false
        : istatusen.every(maybe => maybe === "success");
    const status =
        error ? "error" :
            allSuccess ? "success" : "pending";
    return {data, status, error};
}